import React, { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Moon, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import { useTheme } from '@hooks/useTheme';
import { useIsPhone } from '@hooks/useMediaQuery';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { useMerchantTimezone } from '@/contexts/MerchantTimezoneContext';
import { formatPhoneToday } from '@lib/datetime';
import { resolveBreadcrumb } from './sidebar-config';
import ShellClock from './ShellClock';
import LanguageSwitcher from './LanguageSwitcher';
import { BrandMark, useBrandName } from './SidebarBrand';

interface HeaderBarProps {
  /** Collapses / expands the sidebar (#2511); the phone bar has no such button. */
  onMenuClick?: () => void;
  /** The search box (#2477), injected by the app so layout imports no feature. */
  search?: ReactNode;
  className?: string;
}

/** 「分组 / 页面」 for the lit sidebar row; nothing where no row is lit (#2476 T2). */
function Breadcrumb() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isAdmin } = useAuth();
  const crumb = resolveBreadcrumb(pathname, isAdmin);
  if (crumb === null) return null;
  return (
    <span
      data-testid="app-topbar-breadcrumb"
      className="hidden lg:inline text-[13px] text-muted-foreground whitespace-nowrap"
    >
      {crumb.sectionKey !== null && `${t(crumb.sectionKey)} / `}
      <b className="font-medium text-foreground">{t(crumb.titleKey)}</b>
    </span>
  );
}

/** 「今天 10/1 周四 · 15:20 PDT」, merchant timezone, refreshed each minute. */
function PhoneToday() {
  const { t, i18n } = useTranslation();
  const { timezone, abbreviation } = useMerchantTimezone();
  const now = useMinuteNow();
  return (
    <span className="text-[12.5px] text-muted-foreground">
      {formatPhoneToday(new Date(now), timezone, abbreviation, i18n.language, t)}
    </span>
  );
}

/** The phone top bar (#2487 H1-H3): brand, tenant name, today, theme and language. */
function PhoneHeader({ theme, toggleTheme }: ReturnType<typeof useTheme>) {
  const { t } = useTranslation();
  const name = useBrandName();
  return (
    <header
      data-testid="phone-header"
      className="flex items-center gap-2.5 px-4 pt-3.5 pb-2.5 text-sm bg-background shrink-0"
    >
      <BrandMark size={30} testIdPrefix="phone-brand" />
      <span className="flex flex-1 flex-col leading-tight min-w-0">
        <span className="font-semibold truncate">{name}</span>
        <PhoneToday />
      </span>
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={t('theme.toggle')}
        title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
        className="flex items-center justify-center w-11 h-11 rounded-lg text-muted-foreground cursor-pointer transition-colors hover:bg-surface-2 hover:text-foreground"
      >
        <Moon className="w-[18px] h-[18px]" />
      </button>
      <LanguageSwitcher className="size-11" />
    </header>
  );
}

/**
 * The v4 top bar (#2476): breadcrumb, search, clock, theme and language.
 * The account menu lives in the sidebar's user block.
 * `useTheme` is mounted here and nowhere else (`useTheme.ts`); on a phone the
 * same instance drives the phone branch (#2487), chosen in JS so only one bar
 * is ever mounted.
 */
const HeaderBar: React.FC<HeaderBarProps> = ({ onMenuClick, search }) => {
  const themeState = useTheme();
  const { theme, toggleTheme } = themeState;
  const isPhone = useIsPhone();
  const { t } = useTranslation();

  if (isPhone) return <PhoneHeader {...themeState} />;

  return (
    <header
      data-testid="app-topbar"
      className="flex items-center gap-3 h-[52px] px-7 border-b border-border/60 bg-background shrink-0"
    >
      <button
        type="button"
        aria-label={t('nav.openMenu')}
        title={t('nav.openMenu')}
        className="-ml-1 flex items-center justify-center w-[34px] h-[34px] shrink-0 rounded-lg text-muted-foreground cursor-pointer transition-colors hover:bg-surface-2 hover:text-foreground"
        onClick={onMenuClick}
      >
        <PanelLeft className="w-4 h-4" />
      </button>
      <Breadcrumb />

      {/* Fills the row below lg and pushes right at lg; stays as the spacer when
          the slot is empty (no search permission). */}
      <div className="flex flex-1 min-w-0 lg:flex-none lg:ml-auto">{search}</div>

      <ShellClock className="hidden lg:inline text-[13px] text-muted-foreground whitespace-nowrap" />

      <button
        type="button"
        onClick={toggleTheme}
        aria-label={t('theme.toggle')}
        title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
        className="flex items-center justify-center w-[34px] h-[34px] rounded-lg text-muted-foreground cursor-pointer transition-colors hover:bg-surface-2 hover:text-foreground"
      >
        <Moon className="w-[18px] h-[18px]" />
      </button>
      <LanguageSwitcher className="size-[34px]" />
    </header>
  );
};

export default HeaderBar;
